import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Notebook, Plus, Trash, PencilSimple, Copy, X,
  Phone, MagnifyingGlass, Funnel, Check, Notepad
} from "@phosphor-icons/react";

const SCRIPT_TYPES = [
  { value: "outbound_call", label: "Outbound Call" },
  { value: "follow_up", label: "Follow-Up Call" },
  { value: "objection_handling", label: "Objection Handling" },
  { value: "email_template", label: "Email Template" },
];

const SECTOR_OPTIONS = [
  { value: "all", label: "All Sectors" },
  { value: "education_childcare", label: "Education & Childcare" },
  { value: "healthcare", label: "Healthcare" },
  { value: "technology", label: "IT & Technology" },
  { value: "finance_professional", label: "Finance & Professional" },
  { value: "engineering_construction", label: "Engineering & Construction" },
];

const ROLE_OPTIONS = [
  { value: "all", label: "All Roles" },
  { value: "opportunity_finder", label: "Opportunity Finder" },
  { value: "candidate_finder", label: "Candidate Finder" },
  { value: "senior_recruitment_partner", label: "Senior Recruitment Partner" },
  { value: "team_leader", label: "Team Leader" },
];

export default function ScriptsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "team_leader";
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterSector, setFilterSector] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedScript, setSelectedScript] = useState(null);
  const [copied, setCopied] = useState("");

  const [form, setForm] = useState({
    name: "", description: "", sector: "all", target_role: "all",
    script_type: "outbound_call", opening: "", questions: "", objections: "", closing: "",
  });

  const fetchScripts = useCallback(async () => {
    try {
      const params = filterSector && filterSector !== "all" ? `?sector=${filterSector}` : "";
      const { data } = await api.get(`/scripts${params}`);
      setScripts(data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterSector]);

  useEffect(() => { fetchScripts(); }, [fetchScripts]);

  function resetForm() {
    setForm({ name: "", description: "", sector: "all", target_role: "all", script_type: "outbound_call", opening: "", questions: "", objections: "", closing: "" });
    setEditing(null);
    setShowForm(false);
  }

  function startEdit(script) {
    setForm({
      name: script.name, description: script.description || "",
      sector: script.sector || "all", target_role: script.target_role || "all",
      script_type: script.script_type || "outbound_call",
      opening: script.sections?.opening || "", questions: script.sections?.questions || "",
      objections: script.sections?.objections || "", closing: script.sections?.closing || "",
    });
    setEditing(script.id);
    setShowForm(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Script name is required"); return; }
    try {
      if (editing) {
        await api.put(`/scripts/${editing}`, form);
        toast.success("Script updated");
      } else {
        await api.post("/scripts", form);
        toast.success("Script created");
      }
      resetForm();
      fetchScripts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this script?")) return;
    try {
      await api.delete(`/scripts/${id}`);
      toast.success("Script deleted");
      if (selectedScript?.id === id) setSelectedScript(null);
      fetchScripts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  }

  function copySection(text, section) {
    navigator.clipboard.writeText(text);
    setCopied(section);
    setTimeout(() => setCopied(""), 2000);
    toast.success("Copied to clipboard");
  }

  const filtered = scripts.filter(s =>
    (!search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.description || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="scripts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Recruitment Scripts</h1>
          <p className="text-[#6B7280] text-sm mt-1">Call scripts, objection handling, and email templates</p>
        </div>
        {isAdmin && (
          <Button
            className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] gap-2 font-semibold"
            onClick={() => { resetForm(); setShowForm(true); }}
            data-testid="create-script-btn"
          >
            <Plus size={16} /> New Script
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            placeholder="Search scripts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="scripts-search"
          />
        </div>
        <Select value={filterSector} onValueChange={setFilterSector}>
          <SelectTrigger className="w-[200px]" data-testid="sector-filter">
            <Funnel size={14} className="mr-1 text-[#6B7280]" />
            <SelectValue placeholder="All Sectors" />
          </SelectTrigger>
          <SelectContent>
            {SECTOR_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Script List */}
        <div className="lg:col-span-1 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-[#9CA3AF]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 text-center">
              <Notebook size={40} className="text-[#D1D5DB] mx-auto mb-3" />
              <p className="text-[#6B7280] text-sm">No scripts yet. {isAdmin ? "Create your first script!" : ""}</p>
            </div>
          ) : filtered.map(script => (
            <button
              key={script.id}
              onClick={() => setSelectedScript(script)}
              className={`w-full text-left bg-white rounded-xl border p-4 transition-all hover:border-[#C5A059] ${
                selectedScript?.id === script.id ? "border-[#C5A059] ring-1 ring-[#C5A059]/30" : "border-[#E5E7EB]"
              }`}
              data-testid={`script-card-${script.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-[#0F172A] text-sm">{script.name}</h3>
                  {script.description && <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{script.description}</p>}
                </div>
                <Phone size={16} className="text-[#C5A059] shrink-0 mt-0.5" />
              </div>
              <div className="flex gap-2 mt-2 flex-wrap">
                {script.sector && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#374151] font-medium">
                    {SECTOR_OPTIONS.find(s => s.value === script.sector)?.label || script.sector}
                  </span>
                )}
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#B45309] font-medium">
                  {SCRIPT_TYPES.find(t => t.value === script.script_type)?.label || script.script_type}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Script Detail / Preview */}
        <div className="lg:col-span-2">
          {selectedScript ? (
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="script-detail">
              <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#0F172A]">{selectedScript.name}</h2>
                  {selectedScript.description && <p className="text-sm text-[#6B7280]">{selectedScript.description}</p>}
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="gap-1" onClick={() => startEdit(selectedScript)} data-testid="edit-script-btn">
                      <PencilSimple size={14} /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1 text-[#C5A059] border-[#C5A059] hover:bg-amber-50" onClick={async () => {
                      try {
                        const { data } = await api.post(`/scripts/${selectedScript.id}/add-to-dialler`);
                        toast.success(data.message || "Added to dialler");
                      } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                    }} data-testid="add-to-dialler-btn">
                      <Notepad size={14} /> Add to Dialler
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(selectedScript.id)} data-testid="delete-script-btn">
                      <Trash size={14} /> Delete
                    </Button>
                  </div>
                )}
              </div>
              <div className="p-6 space-y-5">
                {[
                  { key: "opening", label: "Opening", color: "#16A34A" },
                  { key: "questions", label: "Key Questions", color: "#2563EB" },
                  { key: "objections", label: "Objection Handling", color: "#D97706" },
                  { key: "closing", label: "Closing", color: "#7C3AED" },
                ].map(section => {
                  const text = selectedScript.sections?.[section.key] || "";
                  if (!text) return null;
                  return (
                    <div key={section.key}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: section.color }}>{section.label}</span>
                        <button
                          className="text-xs text-[#6B7280] hover:text-[#0F172A] flex items-center gap-1"
                          onClick={() => copySection(text, section.key)}
                        >
                          {copied === section.key ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                        </button>
                      </div>
                      <div className="bg-[#F9FAFB] rounded-lg p-4 text-sm text-[#374151] leading-relaxed whitespace-pre-wrap">
                        {text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center">
              <Notebook size={48} className="text-[#D1D5DB] mx-auto mb-3" />
              <p className="text-[#6B7280]">Select a script to preview</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4" data-testid="script-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
              <h3 className="text-lg font-bold text-[#0F172A]">{editing ? "Edit Script" : "Create New Script"}</h3>
              <button onClick={resetForm}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Script Name *</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Education Cold Call" className="mt-1" data-testid="script-name-input" />
                </div>
                <div>
                  <Label>Script Type</Label>
                  <Select value={form.script_type} onValueChange={v => setForm({...form, script_type: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{SCRIPT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sector</Label>
                  <Select value={form.sector} onValueChange={v => setForm({...form, sector: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select sector" /></SelectTrigger>
                    <SelectContent>{SECTOR_OPTIONS.filter(s => s.value).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Role</Label>
                  <Select value={form.target_role} onValueChange={v => setForm({...form, target_role: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description" className="mt-1" />
              </div>
              {[
                { key: "opening", label: "Opening Script", placeholder: "Good morning, my name is [Your Name] from Banks Morgan..." },
                { key: "questions", label: "Key Questions", placeholder: "What positions are you currently looking to fill?..." },
                { key: "objections", label: "Objection Handling", placeholder: "If they say 'We don't use agencies'..." },
                { key: "closing", label: "Closing Script", placeholder: "Thank you for your time. I'll send over..." },
              ].map(section => (
                <div key={section.key}>
                  <Label>{section.label}</Label>
                  <textarea
                    value={form[section.key]}
                    onChange={e => setForm({...form, [section.key]: e.target.value})}
                    placeholder={section.placeholder}
                    rows={4}
                    className="mt-1 w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none resize-none"
                    data-testid={`script-${section.key}-input`}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1 bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-semibold" data-testid="save-script-btn">
                  {editing ? "Update Script" : "Create Script"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
